import { useState, useMemo, useEffect, useCallback } from 'react';
import { usePageTitle } from '../../../hooks/usePageTitle';
import { useDrawerDrag } from '../../../hooks/useDrawerDrag';
import useMediaQuery from '../../../hooks/useMediaQuery';
import { TriangleAlert, X, ArrowDownUp, ArrowUp, ArrowDown, CircleCheck } from 'lucide-react';
import DashboardLayout from '../../../layouts/DashboardLayout/DashboardLayout';
import SearchInput from '../../../components/SearchInput/SearchInput';
import Button from '../../../components/Button/Button';
import Pagination from '../../../components/Pagination/Pagination';
import PageFooter from '../../../components/PageFooter/PageFooter';
import StatusTooltip from '../../../components/StatusTooltip/StatusTooltip';
import InfoTooltip from '../../../components/InfoTooltip/InfoTooltip';
import TradeCopierMaster from './TradeCopierMaster';
import PowIndex from './PowIndex';
import StartCopying from './StartCopying';
import RiskSlider from '../../../components/RiskSlider/RiskSlider';
import RiskLevelGuide from '../../../components/RiskLevelGuide/RiskLevelGuide';
import RISK_LEVELS, { levelsFor, levelForMult, multFor } from './riskLevels';
import api from '../../../services/api';
import styles from './TradeCopier.module.css';

/* The page is one long scroll now, so the copied accounts are paged at a fixed
   size rather than measured against the window — the same six rows the trade
   tables above them show. */
const ROWS_PAGE_SIZE = 6;

/* The account states that still have a copy to start on them: `available` is an
   account with no copier behind it — never connected, or since disconnected —
   and `settingup` is one partway through the journey. Anything else is already
   copying, and is adjusted through Manage rather than started again. */
const IDLE_SLAVE_STATUSES = ['available', 'settingup'];

/* What a copier's stored multiplier is called. The column names the risk level
   rather than the lot multiplier behind it, because the level is what the user
   picked and what Manage moves them between; the multiplier is the grid's
   answer to it, and the same 2x is Standard on a $200K account and Moderate on
   a $100K one — so the deposit has to come along to read it. */
const riskLabel = (row) => levelForMult(row.mult, row.deposit)?.label ?? '-';

/* The phone's Mult. column: the same level, in the short form the slider uses
   (CON / STD / MOD / AGG), since four full names don't fit beside the status
   and Manage at 375px. */
const riskShort = (row) => levelForMult(row.mult, row.deposit)?.short ?? '-';

/* Every account is a POW one, so on a phone the prefix is dropped to give the
   column back its width — "POW DFY #250" reads as "DFY #250". */
const shortAccount = (name) => String(name || '').replace(/^POW\s+/i, '');

/* Where that level sits on the scale, for the sort. Ranking by the multiplier
   would order the column by a number the column no longer shows, and would put
   a $200K Conservative (1x) level with a $100K Standard (1x). */
const riskRank = (row) => {
  const level = levelForMult(row.mult, row.deposit);
  const i = level ? RISK_LEVELS.findIndex(l => l.id === level.id) : -1;
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
};

function StatusBadge({ status }) {
  if (status === 'disconnected') {
    return (
      <StatusTooltip label="Disconnected">
        <span className={styles.statusDisconnected}>
          <span className={styles.disconnectedDot} />
          <span className={styles.badgeLabel}>Disconnected</span>
        </span>
      </StatusTooltip>
    );
  }
  const known = status === 'connected' || status === 'error';
  const cls = known ? status : 'connected';
  const label = status === 'error' ? 'Error' : status === 'connecting' ? 'Connecting…' : 'Connected';
  return (
    <StatusTooltip label={label}>
      <span className={`${styles.statusBadge} ${styles[`status_${cls}`]}`}>
        <span className={`${styles.statusDot} ${styles[`dot_${cls}`]}`} />
        <span className={styles.badgeLabel}>{label}</span>
      </span>
    </StatusTooltip>
  );
}

function WarningBanner({ text }) {
  return (
    <div className={styles.warningBanner}>
      <TriangleAlert size={14} className={styles.warningIcon} />
      {text || 'Increasing the multiplier means larger copied positions. Review your risk tolerance before saving.'}
    </div>
  );
}

function TradeCopier() {
  usePageTitle('Trade Copier');
  const { isAdmin } = JSON.parse(localStorage.getItem('user') || '{}');
  const [isMaster] = useState(!!isAdmin);
  // The copied accounts condense on a phone: short names, short levels, no
  // search. Same breakpoint as the stylesheet's mobile block.
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);
  const [sort, setSort]     = useState({ col: null, dir: 'asc' });

  // Data from the follower endpoints
  const [copiers, setCopiers]           = useState([]);
  const [copiersLoading, setCopiersLoading] = useState(true);
  const [copiersError, setCopiersError] = useState('');
  const [masters, setMasters]           = useState([]);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [mastersError, setMastersError] = useState('');
  const [slaveAccounts, setSlaveAccounts] = useState([]);
  const [addLoading, setAddLoading]     = useState(false);
  const [addError, setAddError]         = useState('');

  const handleSort = (col) => {
    setSort(s => s.col === col
      ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { col, dir: 'asc' }
    );
  };

  const [manageRow, setManageRow]         = useState(null);
  /* The sheet moves between risk levels, not between arbitrary multipliers: the
     lot multiplier is a level read against the account's starting balance, so
     the only values it may take are the ones the grid gives this account. */
  const [levelId, setLevelId]             = useState(null);
  const [savedMult, setSavedMult]         = useState(1.0);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  /* The receipt shown after a level change: the level it was on and the one it
     is on now. Both null while there is nothing to report. */
  const [changedFrom, setChangedFrom]     = useState(null);
  const [changedTo, setChangedTo]         = useState(null);
  const [guideOpen, setGuideOpen]         = useState(false);
  const [manageLoading, setManageLoading] = useState(false);
  const [manageError, setManageError]     = useState('');

  /* The levels this account may be moved between — everything the grid gives
     its balance. */
  const manageLevels = useMemo(() => levelsFor(manageRow?.deposit), [manageRow]);

  /* Always one of the stops above, never merely whatever `levelId` says: a
     stored value that resolved to a level outside the list has to read as the
     nearest one the account may use. */
  const levelIndex  = Math.max(0, manageLevels.findIndex(l => l.id === levelId));
  const manageLevel = manageLevels[levelIndex] || null;
  /* What this level actually copies at — the figure that reaches the API. */
  const multiplier  = manageLevel ? multFor(manageLevel.id, manageRow?.deposit) : savedMult;

  /* The line under the master's name in the sheet. Read off the master the row
     follows, matched by the name the copier carries, so it describes that
     strategy rather than a figure typed into the markup. Omitted entirely when
     the master isn't in the list — a wrong number is worse than none. */
  const followedMaster = masters.find(m => m.name === manageRow?.following) || null;
  const manageStats = [
    followedMaster?.winRate != null ? `${String(followedMaster.winRate).replace('%', '')}% win rate` : null,
    followedMaster?.growth != null ? `${String(followedMaster.growth).replace('%', '')}% growth` : null,
    followedMaster?.trackMonths != null ? `${followedMaster.trackMonths} mo` : (followedMaster?.track || null),
  ].filter(Boolean).join(' · ');

  /* ── Switch master ──
     Switching is not something the portal can do on its own: the desk has to
     move the copier over. So this collects the two accounts and files it as a
     Help Center ticket, which both notifies the team and leaves the user a
     record of the request under Help Center → tickets. */
  const [switchStep, setSwitchStep]       = useState(null); // null | 'form' | 'done'
  const [switchSlaveId, setSwitchSlaveId] = useState(null); // a copier row id
  const [switchMasterId, setSwitchMasterId] = useState('');
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchError, setSwitchError]     = useState('');
  // Captured at submit, so the confirmation still reads correctly once the
  // selections behind it have been cleared.
  const [switchDone, setSwitchDone]       = useState(null);

  // The row the dropdown currently points at — the one Manage was opened on
  // until the user picks another.
  const switchSlaveRow = useMemo(
    () => copiers.find(c => c.id === switchSlaveId) || null,
    [copiers, switchSlaveId]
  );

  /* Every master except the one this slave already follows — "switching" to the
     current master is not a request the desk can action. Matched by name, which
     is what the copier row carries. */
  const switchMasterOptions = useMemo(
    () => masters.filter(m => m.name !== switchSlaveRow?.following),
    [masters, switchSlaveRow]
  );

  function openSwitch() {
    setSwitchSlaveId(manageRow?.id ?? null);
    setSwitchMasterId('');
    setSwitchError('');
    setSwitchStep('form');
  }

  // Back returns to the Manage sheet, which is still mounted behind this one.
  function backToManage() { setSwitchStep(null); setSwitchError(''); }


  function closeSwitch() {
    setSwitchStep(null);
    setSwitchError('');
    setSwitchDone(null);
    closeManage();
  }

  const { modalRef: switchDrawerRef, dragHandleProps: switchDragHandle } = useDrawerDrag(closeSwitch);

  async function handleSwitchSubmit() {
    const master = masters.find(m => m.id === switchMasterId);
    if (!switchSlaveRow || !master) return;

    setSwitchLoading(true); setSwitchError('');
    try {
      /* One paragraph rather than a list: the API derives the ticket's subject
         from the first 80 characters, so the opening sentence has to stand on
         its own as a summary. */
      const fd = new FormData();
      fd.append('category', 'Trade Copier');
      fd.append('description',
        `Switch master request: please move slave ${switchSlaveRow.account}` +
        `${switchSlaveRow.login ? ` (login ${switchSlaveRow.login})` : ''}` +
        ` from master ${switchSlaveRow.following} to master ${master.name}.` +
        ` Submitted from the Trade Copier.`
      );
      await api.post('/help/conversations', fd);

      setSwitchDone({ slave: switchSlaveRow.account, master: master.name });
      setSwitchStep('done');
    } catch (err) {
      setSwitchError(err.response?.data?.error || 'Failed to submit your request. Please try again.');
    } finally {
      setSwitchLoading(false);
    }
  }

  /* The Start copying journey: open or not. Which step it is on, which account
     and which risk level are picked all live inside it — this screen only needs
     to know the master it was opened for, and hears back once at the end. */
  const [addOpen, setAddOpen]     = useState(false);
  const [addMaster, setAddMaster] = useState(null);

  const fetchCopiers = useCallback(async () => {
    try {
      const { data } = await api.get('/copier/copiers');
      setCopiers(Array.isArray(data) ? data : []);
      setCopiersError('');
    } catch {
      setCopiersError('Failed to load copiers');
    } finally {
      setCopiersLoading(false);
    }
  }, []);

  const fetchMastersAndSlaves = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get('/copier/available-masters'),
        api.get('/copier/slaves'),
      ]);
      setMasters(Array.isArray(m.data) ? m.data : []);
      setSlaveAccounts(Array.isArray(s.data) ? s.data : []);
      setMastersError('');
    } catch {
      // The Masters tab is built from this call, so the failure has to show
      // there; inside the copy modal it surfaces as addError instead.
      setMastersError('Failed to load master accounts');
    } finally {
      setMastersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isMaster) return;
    fetchCopiers();
    fetchMastersAndSlaves();
  }, [isMaster, fetchCopiers, fetchMastersAndSlaves]);

  const filtered = useMemo(() => {
    // A phone has no search field, so a query typed on the desktop layout
    // before a resize mustn't go on hiding rows nobody can see the reason for.
    const q = isMobile ? '' : search.trim().toLowerCase();
    const base = q
      ? copiers.filter(r => (r.account || '').toLowerCase().includes(q))
      : copiers;
    if (!sort.col) return base;
    return [...base].sort((a, b) => {
      const cmp = riskRank(a) - riskRank(b);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [copiers, search, sort, isMobile]);

  /* The strategy the page is built around: whichever master the API offers
     first. It used to be resolved by the name "POW Index", which meant the page
     stood empty on any environment where the desk had called the account
     something else — so the name is no longer part of it. The page still names
     itself after whatever master it lands on — POW Index, or the user's CEM
     master when the API puts one first. */
  const featuredMaster = masters[0] || null;

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * ROWS_PAGE_SIZE, currentPage * ROWS_PAGE_SIZE);

  function openManage(row) {
    /* Opens on the level its stored multiplier corresponds to. A value that
       isn't a grid point — an admin's bulk import, or a figure set before the
       grid existed — reads as the nearest level rather than nothing, so the
       sheet always has a position to start from. */
    setManageRow(row);
    setLevelId(levelForMult(row.mult, row.deposit)?.id ?? null);
    setSavedMult(row.mult);
    setManageError('');
  }

  function closeManage() {
    setManageRow(null); setDisconnectOpen(false); setGuideOpen(false); setManageError('');
  }
  const { modalRef: tcDrawerRef, dragHandleProps: tcDragHandle } = useDrawerDrag(closeManage);

  /* PATCH /copier/copiers/:id { riskValue } — move the copier to the level the
     slider is on. There is no separate confirmation step: the warning sits in
     the sheet above the button, so Save is the acknowledgement, and what
     follows is a receipt of what changed rather than a question. */
  async function handleSaveRisk() {
    if (multiplier === savedMult) return;
    const from = levelForMult(savedMult, manageRow.deposit);
    setManageLoading(true); setManageError('');
    try {
      await api.patch(`/copier/copiers/${manageRow.id}`, { riskValue: multiplier });
      await fetchCopiers();
      setChangedFrom(from);
      setChangedTo(manageLevel);
      setSavedMult(multiplier);
    } catch (err) {
      setManageError(err.response?.data?.error || 'Failed to update risk tolerance');
    } finally {
      setManageLoading(false);
    }
  }

  // Dismisses the receipt and the sheet behind it — the change is already made.
  function closeChanges() {
    setChangedFrom(null); setChangedTo(null); setManageRow(null);
  }

  // DELETE /copier/copiers/:id — disconnect (removes the copier link).
  async function handleDisconnect() {
    setManageLoading(true); setManageError('');
    try {
      await api.delete(`/copier/copiers/${manageRow.id}`);
      // Slaves too, not just copiers: disconnecting hands the account back as
      // `available`, which is what brings the Start copying CTA back.
      await Promise.all([fetchCopiers(), fetchMastersAndSlaves()]);
      closeManage();
    } catch (err) {
      setManageError(err.response?.data?.error || 'Failed to disconnect account');
    } finally {
      setManageLoading(false);
    }
  }

  /* The Start copying journey. POW Index is the only master copied, so it is
     already chosen when the journey opens — the steps ask for the account and
     the risk tolerance. The page may feature the user's own CEM master instead,
     whose `copyMasterId` still points the copy at POW Index. */
  function openAddSlave(master) {
    setAddMaster(master?.copyMasterId ?? master?.id ?? null);
    setAddError('');
    fetchMastersAndSlaves();
    setAddOpen(true);
  }
  function closeAddSlave() { setAddOpen(false); setAddError(''); }

  // Called once, from the journey's last step: everything before it was a
  // selection, this is the connection.
  async function handleStartCopying({ slaveId, riskValue }) {
    if (!addMaster || !slaveId) return;
    setAddLoading(true); setAddError('');
    try {
      await api.post('/copier/copiers', {
        masterId: addMaster,
        slaveId,
        riskValue,
        copyExisting: 'no',
      });
      await Promise.all([fetchCopiers(), fetchMastersAndSlaves()]);
      setAddOpen(false);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to start copying');
    } finally {
      setAddLoading(false);
    }
  }

  /* Who the Start copying CTA is for: anyone holding a trading account that is
     not on the copier yet. Not "anyone with no copiers" — someone already
     copying on one account and sitting on a second, idle one still has a copy
     to start, and the CTA is how they start it. It goes only once every account
     they hold is connected, at which point there is nothing left to offer. */
  const hasIdleAccount = !mastersLoading
    && slaveAccounts.some(s => IDLE_SLAVE_STATUSES.includes(s.slaveStatus));

  /* The API is serving the strategy from sample data rather than the live
     account (SAMPLE_DATA_COPIER on the backend), so the page has to say so. The
     wording travels with the flag — it's stored beside the figures — so the desk
     can change it without a release; the fallback is only there for an older API
     that sets the flag without sending one. Only the POW Index panels above are
     sampled: the copied accounts below are the user's own either way. */
  const sampleNote = featuredMaster?.sample
    ? (featuredMaster.disclaimer
      || 'This is a demo only. Figures shown are illustrative and do not represent actual or expected trading results.')
    : null;

  const selectedMasterObj = masters.find(m => m.id === addMaster);

  /* ── Copied accounts ──
     The user's own side of the page, under the strategy it copies. Handed to
     PowIndex so it sits above the phone's "In-depth view on desktop" hint. A
     phone gets a condensed table — Account, Mult., Status, Action — with the
     status as a dot that names itself on tap, and no search. */
  const copiedAccounts = (
    <section className={styles.copiedSection}>
      <div className={styles.copiedHead}>
        <h2 className={styles.copiedTitle}>COPIED ACCOUNTS</h2>
        {!isMobile && (
          <SearchInput
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search account"
            className={styles.searchWrap}
          />
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={`${styles.table} ${styles.copiedTable}`}>
          <thead>
            <tr>
              <th className={styles.th}>Account</th>
              <th className={styles.th}>
                <button className={styles.thBtn} onClick={() => handleSort('risk')}>
                  {isMobile ? 'Mult.' : 'Risk tolerance'}
                  {/* Sits inside the sort button so it lands between the
                      label and the arrow; the tooltip swallows the click so
                      reaching for it doesn't re-sort the table. No room for
                      it beside the short header on a phone. */}
                  {!isMobile && (
                    <InfoTooltip text="Your risk tolerance may have been automatically adjusted based on your current available equity as part of our scaling plan. If you'd like to manually increase it or have any questions, please open a ticket through the Help Center." />
                  )}
                  {sort.col === 'risk'
                    ? sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                    : <ArrowDownUp size={12} className={styles.sortIcon} />}
                </button>
              </th>
              <th className={`${styles.th} ${styles.statusCol}`}>Status</th>
              <th className={`${styles.th} ${styles.thAction}`}>Action</th>
            </tr>
          </thead>
          <tbody>
            {copiersLoading ? (
              <tr>
                <td colSpan={4} className={styles.emptyState}>Loading copiers…</td>
              </tr>
            ) : copiersError ? (
              <tr>
                <td colSpan={4} className={styles.emptyState}>{copiersError}</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.emptyState}>
                  {search && !isMobile
                    ? <>No results found for &ldquo;{search}&rdquo;</>
                    : 'No copied accounts yet. Start copying to connect one.'}
                </td>
              </tr>
            ) : pageRows.map(r => (
              <tr key={r.id} className={styles.row} onClick={() => openManage(r)}>
                <td className={`${styles.td} ${styles.tdBold}`}>
                  {isMobile ? shortAccount(r.account) : r.account}
                </td>
                <td className={`${styles.td} ${styles.riskCell}`}>
                  {isMobile ? riskShort(r) : riskLabel(r)}
                </td>
                <td className={`${styles.td} ${styles.statusCol}`}><StatusBadge status={r.status} /></td>
                <td className={`${styles.td} ${styles.thAction}`}>
                  <button className={styles.manageBtn} onClick={e => { e.stopPropagation(); openManage(r); }}>Manage</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} variant="outline" />
      </div>
    </section>
  );


  return (
    <DashboardLayout>
      {isMaster ? <TradeCopierMaster /> : (
      <div className={styles.page}>

        {/* ── POW Index ──
            The whole strategy on one scroll: header, figures, performance,
            monthly returns and its trades. Start copying appears while the user
            still has an account off the copier; adjusting one already on it is
            done through Manage below instead. */}
        {mastersLoading ? (
          <>
            <p className={styles.cardsMessage}>Loading POW Index…</p>
            {copiedAccounts}
          </>
        ) : mastersError ? (
          <>
            <p className={styles.cardsMessage}>{mastersError}</p>
            {copiedAccounts}
          </>
        ) : (
          /* Rendered with or without a master: PowIndex keeps its header and
             headline figures either way and says so itself when the account
             doesn't exist yet, rather than the page collapsing to one line. */
          <PowIndex
            master={featuredMaster}
            showCta={hasIdleAccount}
            onStartCopying={openAddSlave}
          >
            {copiedAccounts}
          </PowIndex>
        )}

        <PageFooter note={sampleNote} />

        {/* ── Manage modal ── */}
        {manageRow && !disconnectOpen && !changedTo && !switchStep && (
          <div className={styles.overlay} onClick={closeManage}>
            <div className={styles.modal} ref={tcDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...tcDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>{manageRow.account}</h2>
                <button className={styles.closeBtn} onClick={closeManage}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <p className={styles.sectionLabel}>FOLLOWING MASTER</p>
                <p className={styles.masterName}>{manageRow.following}</p>
                {manageStats && <p className={styles.masterStats}>{manageStats}</p>}

                <div className={styles.riskHead}>
                  <p className={`${styles.sectionLabel} ${styles.sectionLabelGap}`}>RISK TOLERANCE</p>
                  <button type="button" className={styles.guideLink} onClick={() => setGuideOpen(true)}>
                    Risk level guide
                  </button>
                </div>

                {/* The same control the Start copying journey uses, over the
                    levels this account's balance entitles it to. */}
                <RiskSlider
                  levels={manageLevels}
                  value={manageLevel?.id ?? null}
                  onChange={setLevelId}
                  disabled={manageLoading}
                />

                {/* Stated whichever way the level is moving, and standing in for
                    a separate confirmation step: accepting it is what Save
                    means, which is why the sheet says so before the button. */}
                <div className={styles.riskBanner}>
                  <p className={styles.riskBannerTitle}>Warning: Risk setting change</p>
                  <p className={styles.riskBannerText}>
                    Before changing your risk setting below the system will check for open
                    trades. In rare cases, this check or the TradeSync API may fail, which
                    could result in synchronization or execution errors. By continuing, you
                    acknowledge and accept this risk.
                  </p>
                </div>

                {manageError && <p className={styles.multHint} style={{ color: 'var(--maroon-400, #d66)' }}>{manageError}</p>}
              </div>

              <div className={`${styles.modalFooter} ${styles.riskFooter}`}>
                <button className={styles.disconnectBtn} onClick={() => setDisconnectOpen(true)}>
                  Disconnect this account
                </button>
                <div className={styles.riskActions}>
                  <Button variant="secondary" size="sm" fullWidth={false} disabled={manageLoading} onClick={closeManage}>
                    Back
                  </Button>
                  <Button
                    variant="primary" size="sm" fullWidth={false}
                    disabled={manageLoading || multiplier === savedMult}
                    onClick={handleSaveRisk}
                  >
                    {manageLoading
                      ? 'Saving…'
                      : <>
                          <span className={styles.desktopOnly}>Save</span>
                          <span className={styles.mobileOnly}>Save changes</span>
                        </>}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Switch master: pick the slave + the new master ── */}
        {switchStep === 'form' && (
          <div className={styles.overlay} onClick={closeSwitch}>
            <div className={styles.modal} ref={switchDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...switchDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Switch master</h2>
                <button className={styles.closeBtn} onClick={closeSwitch}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <label className={styles.fieldLabel} htmlFor="switch-slave">Slave</label>
                <select
                  id="switch-slave"
                  className={styles.fieldSelect}
                  value={switchSlaveId ?? ''}
                  onChange={e => {
                    setSwitchSlaveId(e.target.value);
                    // The new-master list is filtered by the slave's current
                    // master, so a stale pick could survive into a list that no
                    // longer offers it.
                    setSwitchMasterId('');
                  }}
                >
                  {copiers.map(c => (
                    <option key={c.id} value={c.id}>{c.account}</option>
                  ))}
                </select>

                <label className={`${styles.fieldLabel} ${styles.fieldLabelGap}`} htmlFor="switch-master">New master</label>
                <select
                  id="switch-master"
                  className={styles.fieldSelect}
                  value={switchMasterId}
                  onChange={e => setSwitchMasterId(e.target.value)}
                >
                  <option value="">Select master account</option>
                  {switchMasterOptions.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>

                <div className={styles.switchWarnGap}>
                  <WarningBanner text="This process will take up 24 hours." />
                </div>
                {switchError && <p className={styles.multHint} style={{ color: 'var(--maroon-400, #d66)' }}>{switchError}</p>}
                <hr className={styles.divider} />
              </div>

              <div className={`${styles.modalFooter} ${styles.footerNoRule} ${styles.rowFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} disabled={switchLoading} onClick={backToManage}>Back</Button>
                <Button
                  variant="primary" size="sm" fullWidth={false}
                  disabled={!switchSlaveId || !switchMasterId || switchLoading}
                  onClick={handleSwitchSubmit}
                >{switchLoading ? 'Submitting…' : 'Switch'}</Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Switch master: request filed ── */}
        {switchStep === 'done' && (
          <div className={styles.overlay} onClick={closeSwitch}>
            <div className={`${styles.modal} ${styles.sheetWithClose}`} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Switch master</h2>
                <button className={styles.closeBtn} onClick={closeSwitch}><X size={20} /></button>
              </div>
              <hr className={styles.headerDivider} />

              <div className={styles.modalBody}>
                <p className={styles.sectionLabel}>CONFIRMATION</p>
                <div className={styles.confirmBox}>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Slave account</span>
                    <span className={styles.confirmValue}>{switchDone?.slave}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>New master</span>
                    <span className={styles.confirmValue}>{switchDone?.master}</span>
                  </div>
                </div>

                <div className={styles.successBanner}>
                  <CircleCheck size={18} className={styles.successIcon} />
                  <span>
                    Your master switch request has been successfully submitted.
                    Our team will respond within 24 hours.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Disconnect confirmation ── */}
        {disconnectOpen && (
          <div className={styles.overlay} onClick={closeManage}>
            <div className={styles.modal} ref={tcDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...tcDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Disconnect this account?</h2>
                <button className={styles.closeBtn} onClick={closeManage}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <p className={styles.disconnectBody}>
                  Disconnecting will pause copy trading on <strong>{manageRow?.account}</strong>. No trades will be closed automatically - open copied trades remain open and unmanaged.
                </p>
                <p className={styles.disconnectSub}>You can reconnect this account at any time.</p>
                {manageError && <p className={styles.multHint} style={{ color: 'var(--maroon-400, #d66)' }}>{manageError}</p>}
                <hr className={styles.divider} />
              </div>

              <div className={`${styles.modalFooter} ${styles.rowFooter}`}>
                <Button variant="secondary" size="sm" fullWidth={false} disabled={manageLoading} onClick={() => setDisconnectOpen(false)}>Cancel</Button>
                <Button variant="ghost" size="sm" fullWidth={false} disabled={manageLoading} onClick={handleDisconnect}>{manageLoading ? 'Disconnecting…' : 'Disconnect'}</Button>
              </div>
            </div>
          </div>
        )}

        {/* Opened from the sheet's "Risk level guide" link, over it. */}
        {guideOpen && manageRow && (
          <RiskLevelGuide onBack={() => setGuideOpen(false)} onClose={closeManage} />
        )}

        {/* ── Changes updated ──
            A receipt, not a question: the change is already saved by the time
            this shows, so its only action is to dismiss it. */}
        {changedTo && (
          <div className={styles.overlay} onClick={closeChanges}>
            <div className={styles.modal} ref={tcDrawerRef} onClick={e => e.stopPropagation()}>
              <div className={styles.pullHandle} {...tcDragHandle} />
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle}>Changes updated</h2>
                <button className={styles.closeBtn} onClick={closeChanges}><X size={20} /></button>
              </div>

              <div className={styles.modalBody}>
                <p className={styles.sectionLabel}>DETAILS</p>
                <div className={styles.confirmBox}>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>Previous risk tolerance</span>
                    <span className={styles.confirmValue}>{changedFrom?.label || '-'}</span>
                  </div>
                  <div className={styles.confirmRow}>
                    <span className={styles.confirmLabel}>New risk tolerance</span>
                    <span className={`${styles.confirmValue} ${styles.confirmValueGreen}`}>{changedTo.label}</span>
                  </div>
                </div>
              </div>

              <div className={`${styles.modalFooter} ${styles.doneFooter}`}>
                <Button variant="ghost" size="sm" fullWidth={false} className={styles.doneBtn} onClick={closeChanges}>
                  Done
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Copy slave account: pick the account + multiplier ── */}
        {/* ── Start copying ──
            Three steps in one component: pick the account, pick the risk
            tolerance, confirm. Nothing is connected until its last step calls
            back, so closing it at any point leaves the copier untouched. */}
        {addOpen && (
          <StartCopying
            master={selectedMasterObj}
            slaves={slaveAccounts}
            slavesLoading={mastersLoading}
            loading={addLoading}
            error={addError}
            onClose={closeAddSlave}
            onConfirm={handleStartCopying}
          />
        )}

      </div>
      )}
    </DashboardLayout>
  );
}

export default TradeCopier;
